import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, Users, FolderTree, DollarSign, Clock, Truck, Crown, Percent, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { adminAPI } from '@/lib/api';
import { money, num } from '@/lib/format';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    adminAPI.getStats()
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div data-testid="admin-dashboard-loading" className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-muted rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Products', value: num(stats?.total_products || 0), icon: Package, color: 'text-teal', bg: 'bg-teal/15', to: '/admin/products' },
    { label: 'Total Orders', value: num(stats?.total_orders || 0), icon: ShoppingCart, color: 'text-gold', bg: 'bg-gold/15', to: '/admin/orders' },
    { label: 'Vendors', value: num(stats?.total_suppliers || 0), icon: Truck, color: 'text-teal', bg: 'bg-teal/15', to: '/admin/suppliers' },
    { label: 'Active Vendors', value: num(stats?.active_vendors || 0), icon: Calendar, color: 'text-green-400', bg: 'bg-green-400/15', to: '/admin/windows' },
    { label: 'Customers', value: num(stats?.total_users || 0), icon: Users, color: 'text-teal', bg: 'bg-teal/15', to: '/admin/users' },
    { label: 'Sales', value: `R${money((stats?.total_sales || 0))}`, icon: DollarSign, color: 'text-gold', bg: 'bg-gold/15', to: '/admin/reports' },
    { label: 'Pending Orders', value: num(stats?.pending_orders || 0), icon: Clock, color: 'text-orange-400', bg: 'bg-orange-400/15', to: '/admin/orders?status=under_review' },
    { label: 'VIP Customers', value: num(stats?.vip_customers || 0), icon: Crown, color: 'text-gold', bg: 'bg-gold/15', to: '/admin/users' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Dashboard</h1>
        <p className="font-body text-sm text-foreground/70 mt-1">AVZ Wholesale — Reseller Platform Overview</p>
      </div>

      {/* Active Vendors Info */}
      {stats?.active_vendors > 0 && (
        <div className="mb-6 p-4 rounded-2xl bg-teal/15 border border-teal/30 flex items-center gap-3" data-testid="active-window-info">
          <Calendar className="h-5 w-5 text-teal" />
          <p className="font-body text-sm font-medium text-foreground">
            {stats.active_vendors} vendor{stats.active_vendors !== 1 ? 's' : ''} currently accepting orders
          </p>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <button
            key={i}
            type="button"
            onClick={() => navigate(stat.to)}
            className="text-left p-6 rounded-2xl bg-card border border-border hover:border-teal/50 hover:shadow-lg transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal/40"
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-body text-xs uppercase tracking-[0.15em] text-foreground/60">{stat.label}</span>
              <div className={`p-2 rounded-xl ${stat.bg}`}>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
            </div>
            <p className="font-body text-3xl font-bold text-foreground" data-testid={`stat-value-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>{stat.value}</p>
          </button>
        ))}
      </div>

      {/* Recent Orders */}
      <div className="rounded-2xl bg-card border border-border p-6">
        <h2 className="font-heading text-xl font-medium text-foreground mb-4">Recent Orders</h2>
        {stats?.recent_orders?.length === 0 ? (
          <p className="font-body text-sm text-foreground/50 text-center py-8">No orders yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="recent-orders-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 pb-3">Order</th>
                  <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 pb-3">Customer</th>
                  <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 pb-3">Window</th>
                  <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 pb-3">Status</th>
                  <th className="text-right font-body text-xs uppercase tracking-wider text-foreground/60 pb-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {stats?.recent_orders?.map(order => (
                  <tr
                    key={order.id}
                    onClick={() => navigate(`/admin/orders?order=${order.id}`)}
                    data-testid={`recent-order-${order.id}`}
                    className="border-b border-border/50 last:border-0 hover:bg-muted/40 cursor-pointer transition-colors"
                  >
                    <td className="py-3 font-body text-sm text-foreground">{order.order_number || `#${order.id.slice(0, 8)}`}</td>
                    <td className="py-3 font-body text-sm text-foreground/70">
                      {order.contact_name || order.user_name || order.shipping_name || 'N/A'}
                    </td>
                    <td className="py-3 font-body text-xs text-foreground/50">
                      {order.order_window_name || 'N/A'}
                    </td>
                    <td className="py-3">
                      <Badge
                        className="rounded-full font-body text-xs capitalize"
                        variant={order.status === 'closed' ? 'default' : 'outline'}
                      >
                        {order.status?.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="py-3 text-right font-body text-sm font-semibold text-vintage">R{money(order.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
